import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Trophy, Star, Flame, Award, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconOptions = ['Trophy', 'Star', 'Flame', 'Award', 'Target', 'Medal'];
const colorOptions = ['gold', 'silver', 'bronze', 'blue', 'green', 'purple', 'red'];

const iconMap = { Trophy, Star, Flame, Award, Target, Medal: Trophy };

const colorClasses = {
  gold: 'bg-gradient-to-br from-[#D4AF37] to-[#F4D03F]',
  silver: 'bg-gradient-to-br from-[#C0C0C0] to-[#E8E8E8]',
  bronze: 'bg-gradient-to-br from-[#CD7F32] to-[#E8A84C]',
  blue: 'bg-gradient-to-br from-[#3B82F6] to-[#60A5FA]',
  green: 'bg-gradient-to-br from-[#08513F] to-[#2A9D8F]',
  purple: 'bg-gradient-to-br from-[#8B5CF6] to-[#A78BFA]',
  red: 'bg-gradient-to-br from-[#EF4444] to-[#F87171]'
};

export default function AdminAchievements() {
  const { getAuthHeaders } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Trophy',
    type: 'points',
    requirement: 100,
    color: 'gold',
    is_enabled: true,
    order: 0,
    task_id: '',
    auto_earn_on_creation: false
  });
  const [tasks, setTasks] = useState([]);

  const fetchAchievements = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/achievements/all`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setAchievements(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch achievements:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/all`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAchievements();
    fetchTasks();
  }, [fetchAchievements, fetchTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if ((formData.type === 'specific_task_completion' || formData.type === 'specific_task_streak') && !formData.task_id) {
      toast.error('Please select a task for specific task achievements');
      return;
    }

    if (formData.type === 'specific_task_streak' && formData.task_id) {
      const selectedTask = tasks.find(task => task.task_id === formData.task_id);
      if (selectedTask && selectedTask.frequency !== 'daily') {
        toast.error('Specific task streak achievements can only be created for daily tasks');
        return;
      }
    }

    try {
      const url = editingAchievement 
        ? `${API_URL}/api/achievements/${editingAchievement.achievement_id}`
        : `${API_URL}/api/achievements`;
      
      const response = await fetch(url, {
        method: editingAchievement ? 'PUT' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Handle auto-earn results for both create and update
        if (result.auto_earn_results) {
          const { newly_earned, summary } = result.auto_earn_results;

          if (newly_earned > 0) {
            const action = editingAchievement ? 'updated' : 'created';
            toast.success(`✅ Achievement ${action} and awarded to ${newly_earned} children! ${summary}`);
            
            // Log details for admin visibility
            result.auto_earn_results.earned_details.forEach(detail => {
              console.log(`🏆 ${detail.child_name} earned: ${detail.achievement_name}`);
            });
          } else {
            const action = editingAchievement ? 'updated' : 'created';
            toast.info(`✅ Achievement ${action}. No children met requirements yet.`);
          }
        } else {
          toast.success(editingAchievement ? 'Achievement updated!' : 'Achievement created!');
        }
        
        setDialogOpen(false);
        resetForm();
        fetchAchievements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save achievement');
      }
    } catch (error) {
      toast.error('Failed to save achievement');
    }
  };

  const handleDelete = async (achievementId) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/achievements/${achievementId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Achievement deleted!');
        fetchAchievements();
      }
    } catch (error) {
      toast.error('Failed to delete achievement');
    }
  };

  const resetForm = () => {
    setEditingAchievement(null);
    setFormData({
      name: '',
      description: '',
      icon: 'Trophy',
      type: 'points',
      requirement: 100,
      color: 'gold',
      is_enabled: true,
      order: 0,
      task_id: '',
      auto_earn_on_creation: false
    });
  };

  const openEditDialog = (achievement) => {
    setEditingAchievement(achievement);
    setFormData({
      name: achievement.name,
      description: achievement.description,
      icon: achievement.icon,
      type: achievement.type,
      requirement: achievement.requirement,
      color: achievement.color,
      is_enabled: achievement.is_enabled,
      order: achievement.order,
      task_id: achievement.task_id || '',
      auto_earn_on_creation: achievement.auto_earn_on_creation || false
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Manage Achievements</h1>
            <p className="text-[#6B7280] mt-1">Create and manage badges and rewards</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-white rounded-full px-6"
                data-testid="add-achievement-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Achievement
              </Button>
            </DialogTrigger>
            <DialogContent 
              className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto"
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                  {editingAchievement ? 'Edit Achievement' : 'Create Achievement'}
                </DialogTitle>
                <DialogDescription>
                  {editingAchievement ? 'Update achievement details' : 'Add a new badge'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Achievement Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Super Star"
                    className="rounded-xl"
                    data-testid="achievement-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Reach 100 points"
                    className="rounded-xl"
                    data-testid="achievement-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Icon</Label>
                    <Select
                      value={formData.icon}
                      onValueChange={(value) => setFormData({ ...formData, icon: value })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map((icon) => (
                          <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Select
                      value={formData.color}
                      onValueChange={(value) => setFormData({ ...formData, color: value })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {colorOptions.map((color) => (
                          <SelectItem key={color} value={color} className="capitalize">{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value, task_id: '' })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="points">Points</SelectItem>
                        <SelectItem value="streak">Streak</SelectItem>
                        <SelectItem value="task_completion">Task Completion</SelectItem>
                        <SelectItem value="specific_task_completion">Specific Task</SelectItem>
                        <SelectItem value="specific_task_streak">Specific Task Streak</SelectItem>
                        <SelectItem value="special">Special</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="requirement">Requirement</Label>
                    <Input
                      id="requirement"
                      type="number"
                      min="1"
                      value={formData.requirement}
                      onChange={(e) => setFormData({ ...formData, requirement: parseInt(e.target.value) || "" })}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                {/* Task Selection for Specific Task Types */}
                {(formData.type === 'specific_task_completion' || formData.type === 'specific_task_streak') && (
                  <div className="space-y-2">
                    <Label htmlFor="task_id">Select Task</Label>
                    <Select
                      value={formData.task_id}
                      onValueChange={(value) => setFormData({ ...formData, task_id: value })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select a task" />
                      </SelectTrigger>
                      <SelectContent>
                        {tasks
                          .filter(task => formData.type === 'specific_task_streak' ? task.frequency === 'daily' : true)
                          .map((task) => (
                          <SelectItem key={task.task_id} value={task.task_id}>
                            {task.name} ({task.frequency === 'daily' ? 'Daily' : 'One-time'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-[#FFFBEB] rounded-xl">
                  <Label htmlFor="is_enabled" className="cursor-pointer">
                    Achievement Enabled
                  </Label>
                  <Switch
                    id="is_enabled"
                    checked={formData.is_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-[#F0F9FF] rounded-xl">
                  <div className="flex flex-col">
                    <Label htmlFor="auto_earn_on_creation" className="cursor-pointer">
                      Auto-award on Creation
                    </Label>
                    <p className="text-xs text-[#6B7280] mt-1">
                      Check all children and award achievement if requirements already met
                    </p>
                  </div>
                  <Switch
                    id="auto_earn_on_creation"
                    checked={formData.auto_earn_on_creation}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_earn_on_creation: checked })}
                  />
                </div>

                {/* Preview */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-[#6B7280] mb-2">Preview:</p>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-xl ${colorClasses[formData.color]} flex items-center justify-center`}>
                      {React.createElement(iconMap[formData.icon] || Trophy, { 
                        className: "w-8 h-8 text-white" 
                      })}
                    </div>
                    <div>
                      <p className="font-bold text-[#264653]">{formData.name || 'Achievement Name'}</p>
                      <p className="text-sm text-[#6B7280]">{formData.description || 'Description'}</p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-white rounded-xl h-12"
                  data-testid="save-achievement-btn"
                >
                  {editingAchievement ? 'Update Achievement' : 'Create Achievement'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Achievements Grid */}
        {achievements.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <Trophy className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Achievements Yet</h2>
            <p className="text-[#6B7280]">Create your first badge!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => {
              const IconComponent = iconMap[achievement.icon] || Trophy;
              
              return (
                <Card 
                  key={achievement.achievement_id}
                  className={`bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg overflow-hidden animate-slide-up ${
                    !achievement.is_enabled ? 'opacity-60' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`achievement-card-${achievement.achievement_id}`}
                >
                  <div className={`${colorClasses[achievement.color]} p-6 text-center`}>
                    <IconComponent className="w-12 h-12 text-white mx-auto" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-[#264653] font-outfit">{achievement.name}</h3>
                        {!achievement.is_enabled && (
                          <Badge variant="outline" className="text-gray-500 text-xs">Disabled</Badge>
                        )}
                      </div>
                      <Badge className={`rounded-full text-xs ${
                        achievement.type === 'points' 
                          ? 'bg-[#D4AF37]/10 text-[#B5952F]'
                          : achievement.type === 'streak'
                            ? 'bg-[#E76F51]/10 text-[#E76F51]'
                            : 'bg-[#08513F]/10 text-[#08513F]'
                      }`}>
                        {achievement.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#6B7280] mb-3">{achievement.description}</p>
                    <p className="text-sm font-medium text-[#264653]">
                      Requirement: {achievement.requirement} {achievement.type === 'points' ? 'pts' : achievement.type === 'streak' ? 'days' : 'tasks'}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(achievement)}
                        className="flex-1 rounded-lg"
                        data-testid={`edit-achievement-${achievement.achievement_id}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(achievement.achievement_id)}
                        className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-achievement-${achievement.achievement_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
